import { useState, useRef, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Tag, FileText } from 'lucide-react';
import { useLanguage } from '@/hooks/useLanguage';
import { Input } from '@/components/ui/input';
import { articles, getAllTags } from '@/data/articleIndex';
import { cn } from '@/lib/utils';

interface SearchBarProps {
  onClose?: () => void;
  autoFocus?: boolean;
}

type Suggestion =
  | { kind: 'article'; label: string; slug: string }
  | { kind: 'tag'; label: string; count: number };

export const SearchBar = ({ onClose, autoFocus }: SearchBarProps) => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLFormElement>(null);

  const allTags = useMemo(() => getAllTags(), []);

  useEffect(() => {
    if (autoFocus) {
      inputRef.current?.focus();
    }
  }, [autoFocus]);

  // Close suggestions on outside click
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const suggestions = useMemo<Suggestion[]>(() => {
    const q = query.trim().toLowerCase();
    if (q.length < 2) return [];

    const tagMatches: Suggestion[] = allTags
      .filter(({ tag }) => tag.toLowerCase().includes(q))
      .slice(0, 4)
      .map(({ tag, count }) => ({ kind: 'tag', label: tag, count }));

    const articleMatches: Suggestion[] = articles
      .map(a => {
        const title = a.content.en.title.toLowerCase();
        const idx = title.indexOf(q);
        if (idx === -1) return null;
        return { article: a, idx };
      })
      .filter((x): x is { article: typeof articles[0]; idx: number } => x !== null)
      .sort((a, b) => a.idx - b.idx)
      .slice(0, 6)
      .map(({ article }) => ({
        kind: 'article' as const,
        label: article.content.en.title,
        slug: article.slug,
      }));

    return [...tagMatches, ...articleMatches];
  }, [query, allTags]);

  const submitQuery = (q: string) => {
    if (!q.trim()) return;
    navigate(`/search?q=${encodeURIComponent(q.trim())}`);
    setOpen(false);
    onClose?.();
  };

  const selectSuggestion = (s: Suggestion) => {
    if (s.kind === 'article') {
      navigate(`/article/${s.slug}`);
    } else {
      navigate(`/search?tags=${encodeURIComponent(s.label)}`);
    }
    setOpen(false);
    onClose?.();
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (activeIdx >= 0 && suggestions[activeIdx]) {
      selectSuggestion(suggestions[activeIdx]);
      return;
    }
    submitQuery(query);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setOpen(false);
      onClose?.();
      return;
    }
    if (!open || suggestions.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx(i => (i + 1) % suggestions.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx(i => (i <= 0 ? suggestions.length - 1 : i - 1));
    }
  };

  return (
    <form ref={containerRef} onSubmit={handleSubmit} className="relative" role="search">
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
      <Input
        ref={inputRef}
        type="search"
        placeholder={t('nav.searchPlaceholder')}
        value={query}
        onChange={(e) => { setQuery(e.target.value); setOpen(true); setActiveIdx(-1); }}
        onFocus={() => setOpen(true)}
        onKeyDown={handleKeyDown}
        className="pl-10 bg-secondary/50 border-border focus:bg-background"
        aria-autocomplete="list"
        aria-expanded={open && suggestions.length > 0}
        aria-controls="search-suggestions"
      />

      {open && suggestions.length > 0 && (
        <ul
          id="search-suggestions"
          role="listbox"
          className="absolute left-0 right-0 top-full mt-2 z-50 bg-background border border-border shadow-lg max-h-96 overflow-auto"
        >
          {suggestions.map((s, i) => (
            <li
              key={s.kind + ':' + (s.kind === 'article' ? s.slug : s.label)}
              role="option"
              aria-selected={i === activeIdx}
            >
              <button
                type="button"
                onMouseEnter={() => setActiveIdx(i)}
                onClick={() => selectSuggestion(s)}
                className={cn(
                  'w-full text-left px-4 py-3 flex items-center gap-3 text-sm transition-colors',
                  i === activeIdx ? 'bg-secondary text-primary' : 'text-foreground/80 hover:bg-secondary/60'
                )}
              >
                {s.kind === 'tag' ? (
                  <Tag className="w-3.5 h-3.5 shrink-0 text-muted-foreground" strokeWidth={1.5} />
                ) : (
                  <FileText className="w-3.5 h-3.5 shrink-0 text-muted-foreground" strokeWidth={1.5} />
                )}
                <span className="truncate flex-1">{s.label}</span>
                {s.kind === 'tag' && (
                  <span className="text-[10px] tracking-luxury uppercase text-muted-foreground">
                    {s.count} articles
                  </span>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}
    </form>
  );
};
